// Súper online PR 4 (Tarea 6): el carrito compartido entre portada, pasillos y productos.
//
// Las páginas de pasillo y de producto son cargas completas (`<a>` plano): el carrito
// en memoria de `RestaurantMicrosite` se perdía al salir de la portada. Con la bandera
// `online_retail_v2` —y solo con ella— el carrito vive en `localStorage`, por tienda,
// junto con los DATOS de cada producto que el carrito necesita:
//
//   - el subtotal gravable trata como gravable toda línea que no encuentra en la lista
//     (`micrositeTaxableSubtotal`, lib/microsite/couponUx.ts), y la portada ya solo trae
//     12 productos por pasillo: la leche exenta agregada desde la página 3 se mostraría
//     con impuesto;
//   - el stock del carrito solo pregunta por lo retail que encuentra en la lista
//     (`retailStockItemIds`, components/microsite/useRetailStock.ts).
//
// Por eso cada línea guarda sus datos y la portada los mezcla en `menuItems`
// (`mergeCartFacts`). El cobro lo sigue fijando el servidor (/api/online-orders/session).
//
// Concurrencia: cada escritura guarda una ficha de versión `rev` NUEVA e irrepetible
// (`crypto.randomUUID()`); sin llave guardada la ficha es `null`. El controlador de la
// portada recuerda la última ficha que leyó o escribió y SOLO escribe encima de ésa: si
// otra página (un producto mientras la portada dormía en el bfcache, otra pestaña) guardó
// después, no pisa nada y devuelve lo guardado («stale»). No es un contador a propósito
// (revisión T6, #1): al vaciar el carrito la llave se borra, y un contador volvería a
// empezar en 1, 2… — una portada congelada que recordara el mismo número creería que
// nada cambió, mostraría lo ya pagado y pisaría lo nuevo.
//
// Restaurantes: `makeGroceryCartSync({ enabled: false })` no toca el storage nunca.
import type { CartItem } from "@/components/microsite/types"
import { parsePaymentReturn, type StorageLike } from "@/lib/microsite/hostedPayment"
import { appendCartLine } from "@/lib/microsite/cartLines"
import { isRetailItem } from "@/lib/microsite/storefrontKind"
import type { StorefrontRetailRow } from "./retailRow"

/** Lo que el carrito necesita saber de un producto que quizá no está en la lista cargada. */
export type CartLineFacts = {
  id: string
  name: string
  translations: unknown
  price: number
  image_url: string | null
  category_id: string | null
  is_retail: boolean
  is_taxable: boolean
  pricing_mode: string
  has_variants: boolean
  weight_unit: string | null
  is_available: boolean | null
}

/** Un día (D10): pasado esto el carrito guardado se descarta. */
export const GROCERY_CART_TTL_MS = 24 * 60 * 60_000

export function groceryCartKey(slug: string): string {
  return `redy:carrito:v1:${slug}`
}

type Stored = {
  v: 1
  rev: string
  savedAt: number
  cart: CartItem[]
  facts: Record<string, CartLineFacts>
}

/** `rev` null = no hay nada guardado (llave ausente, vencida o inválida). */
type Snapshot = { rev: string | null; cart: CartItem[]; facts: Record<string, CartLineFacts> }

/** Nuevo en cada llamada: lo leído termina en estado de React y no se comparte. */
const empty = (): Snapshot => ({ rev: null, cart: [], facts: {} })

/** Una ficha de versión que no se repite nunca, ni entre pestañas ni tras vaciar el carrito. */
function newRev(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID()
  } catch {
    /* sin crypto: abajo */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`
}

function defaultStore(): StorageLike | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null
  } catch {
    return null
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v)

/** Un modificador que `micrositeLineSubtotal` puede sumar sin lanzar (revisión final PR 4, #2). */
function isValidModifier(m: unknown): boolean {
  if (!isRecord(m)) return false
  if (
    typeof m.modifier_group_id !== "string" ||
    typeof m.modifier_option_id !== "string" ||
    typeof m.option_name !== "string" ||
    typeof m.price_delta !== "number" ||
    !Number.isFinite(m.price_delta)
  ) {
    return false
  }
  // `quantity` es opcional (undefined = 1); si viene, entero positivo.
  return m.quantity === undefined || (typeof m.quantity === "number" && Number.isInteger(m.quantity) && m.quantity > 0)
}

function isValidLine(l: unknown): l is CartItem {
  if (!isRecord(l)) return false
  return (
    typeof l.cartId === "string" &&
    typeof l.itemId === "string" &&
    typeof l.name === "string" &&
    typeof l.price === "number" &&
    Number.isFinite(l.price) &&
    l.price >= 0 &&
    typeof l.quantity === "number" &&
    Number.isInteger(l.quantity) &&
    l.quantity > 0 &&
    Array.isArray(l.modifiers) &&
    l.modifiers.every(isValidModifier) &&
    // La marca del mercado es opcional (carritos de antes no la traen); si viene, booleano.
    (l.isRetail === undefined || typeof l.isRetail === 'boolean')
  )
}

function isValidFacts(f: unknown): f is CartLineFacts {
  return isRecord(f) && typeof f.id === "string" && typeof f.is_taxable === "boolean"
}

/**
 * Lo guardado para `slug`, con su `rev`; vacío (rev null) si no hay, venció, es de otra
 * versión, está corrupto o el storage lanza. Una línea o un dato mal formado invalida
 * TODO: un carrito a medias cobraría distinto de lo que el cliente armó.
 */
function readSnapshot(slug: string, store: StorageLike | null, now: number): Snapshot {
  try {
    const raw = store?.getItem(groceryCartKey(slug))
    if (!raw) return empty()
    const s = JSON.parse(raw) as Partial<Stored>
    if (!isRecord(s) || s.v !== 1) return empty()
    if (typeof s.rev !== "string" || s.rev.length === 0) return empty()
    if (typeof s.savedAt !== "number" || now - s.savedAt > GROCERY_CART_TTL_MS) return empty()
    if (!Array.isArray(s.cart) || !s.cart.every(isValidLine)) return empty()
    if (!isRecord(s.facts) || !Object.values(s.facts).every(isValidFacts)) return empty()
    return { rev: s.rev, cart: s.cart, facts: s.facts as Record<string, CartLineFacts> }
  } catch {
    return empty()
  }
}

/** Solo los datos de los productos que están en el carrito. */
function factsForCart(cart: readonly CartItem[], facts: Record<string, CartLineFacts>): Record<string, CartLineFacts> {
  const out: Record<string, CartLineFacts> = {}
  for (const l of cart) {
    const f = facts[l.itemId]
    if (f) out[l.itemId] = f
  }
  return out
}

/**
 * Escribe `cart` con una ficha nueva. Carrito vacío = `removeItem` (ficha null). Devuelve
 * la ficha que quedó guardada, o `undefined` si no se pudo escribir: con el storage lleno
 * o bloqueado (Safari privado) el que llama NO debe creer que su ficha es la guardada.
 */
function writeSnapshot(
  slug: string,
  cart: CartItem[],
  facts: Record<string, CartLineFacts>,
  store: StorageLike | null,
  now: number,
): string | null | undefined {
  if (!store) return undefined
  try {
    if (cart.length === 0) {
      store.removeItem(groceryCartKey(slug))
      return null
    }
    const rev = newRev()
    const stored: Stored = { v: 1, rev, savedAt: now, cart, facts: factsForCart(cart, facts) }
    store.setItem(groceryCartKey(slug), JSON.stringify(stored))
    return rev
  } catch {
    return undefined
  }
}

export function readGroceryCart(
  slug: string,
  store: StorageLike | null = defaultStore(),
  now: number = Date.now(),
): { cart: CartItem[]; facts: Record<string, CartLineFacts> } {
  const { cart, facts } = readSnapshot(slug, store, now)
  return { cart, facts }
}

/** Guarda solo los `facts` de los `itemId` del carrito; carrito vacío = `removeItem`. */
export function saveGroceryCart(
  slug: string,
  cart: CartItem[],
  facts: Record<string, CartLineFacts>,
  store: StorageLike | null = defaultStore(),
  now: number = Date.now(),
): void {
  writeSnapshot(slug, cart, facts, store, now)
}

export function clearGroceryCart(slug: string, store: StorageLike | null = defaultStore()): void {
  try {
    store?.removeItem(groceryCartKey(slug))
  } catch {
    /* nada que limpiar */
  }
}

/**
 * «Agregar» desde un pasillo o un producto: relee lo guardado y agrega encima
 * (`appendCartLine`), nunca sobre un estado de React que pudo quedar viejo.
 */
export function addToGroceryCart(
  slug: string,
  line: CartItem,
  facts: CartLineFacts,
  store: StorageLike | null = defaultStore(),
  now: number = Date.now(),
): { cart: CartItem[]; facts: Record<string, CartLineFacts> } {
  const prev = readSnapshot(slug, store, now)
  // Único punto de escritura de pasillo, producto y /buscar: aquí se sella la marca.
  const cart = appendCartLine(prev.cart, { ...line, isRetail: isRetailItem(facts) })
  const merged = { ...prev.facts, [line.itemId]: facts }
  writeSnapshot(slug, cart, merged, store, now)
  return { cart, facts: factsForCart(cart, merged) }
}

type FactsSource =
  | StorefrontRetailRow
  | {
      id: string
      name: string
      translations?: unknown
      price: number
      image_url?: string | null
      category_id?: string | null
      is_retail?: boolean | null
      is_taxable?: boolean | null
      pricing_mode?: string | null
      has_variants?: boolean | null
      weight_unit?: string | null
      is_available?: boolean | null
    }

/** Los datos de una fila. Lo ausente va a su valor de `main`: gravable, no retail, por unidad. */
export function factsOf(row: FactsSource): CartLineFacts {
  return {
    id: row.id,
    name: row.name,
    translations: row.translations ?? null,
    price: row.price,
    image_url: row.image_url ?? null,
    category_id: row.category_id ?? null,
    is_retail: row.is_retail === true,
    is_taxable: row.is_taxable !== false,
    pricing_mode: row.pricing_mode ?? "each",
    has_variants: row.has_variants === true,
    weight_unit: row.weight_unit ?? null,
    is_available: row.is_available ?? null,
  }
}

/**
 * `items` + los datos guardados de lo que NO está en `items`, al final. Sin nada que
 * agregar devuelve `items` tal cual (misma referencia: no rompe los memos del carrito).
 *
 * Un producto con variantes agregado desde otra página llega sin su lista de variantes:
 * se le pone `variants: []` para que `needsRetailPicker` lo reconozca y nunca salga como
 * plan B (revisión final PR 2b, I1: el plan B no sabe de variantes).
 */
export function mergeCartFacts<T extends { id: string }>(
  items: T[],
  facts: Record<string, CartLineFacts>,
): Array<T | CartLineFacts> {
  const have = new Set(items.map((i) => i.id))
  const extra: CartLineFacts[] = []
  for (const f of Object.values(facts)) {
    if (have.has(f.id)) continue
    have.add(f.id)
    extra.push(f.has_variants ? ({ ...f, variants: [] } as CartLineFacts) : f)
  }
  return extra.length === 0 ? items : [...items, ...extra]
}

/**
 * Lo que el re-chequeo del carrito trajo EN ESTA sesión (D10) encima de las filas del carrito:
 * solo `is_taxable` e `is_available`, solo de esos ids. `mergeCartFacts` deja ganar a la fila
 * de la página (un dato guardado de hasta 24 h no le gana a una carga nueva), pero una página
 * abierta hace horas tiene filas más viejas que lo que acaba de devolver cart-availability: sin
 * esto, un producto de la portada que pasó a exento seguiría sumando impuesto (revisión 7b #1).
 * El precio NO se pisa: el de la línea ya lo rearmó `refreshRetailLinePrices`.
 *
 * Sin nada que pisar (vacío, ids ausentes o valores iguales) devuelve `menuItems` tal cual
 * (misma referencia: no rompe los memos del carrito); las filas sin cambio, también.
 */
export function overlayFreshFacts<T extends { id: string }>(
  menuItems: T[],
  fresh: Record<string, Partial<Pick<CartLineFacts, "is_taxable" | "is_available">>>,
): T[] {
  let changed = false
  const out = menuItems.map((item) => {
    const patch = Object.hasOwn(fresh, item.id) ? fresh[item.id] : undefined
    if (!patch) return item
    const row = item as T & Partial<Pick<CartLineFacts, "is_taxable" | "is_available">>
    const next: Partial<Pick<CartLineFacts, "is_taxable" | "is_available">> = {}
    if (patch.is_taxable !== undefined && patch.is_taxable !== row.is_taxable) next.is_taxable = patch.is_taxable
    if (patch.is_available !== undefined && patch.is_available !== row.is_available) next.is_available = patch.is_available
    if (Object.keys(next).length === 0) return item
    changed = true
    return { ...item, ...next }
  })
  return changed ? out : menuItems
}

export type PersistResult = { kind: "saved" } | { kind: "skipped" } | { kind: "stale"; cart: CartItem[] }

export type GroceryCartSync = {
  hydrate(prev: CartItem[]): CartItem[]
  resync(): CartItem[] | null
  facts(): Record<string, CartLineFacts>
  persist(cart: CartItem[], catalog: ReadonlyArray<CartLineFacts>): PersistResult
  updateFacts(fresh: Record<string, Partial<CartLineFacts>>): void
  confirmOrder(): void
}

/** Solo lo que puede refrescarse de un producto (D10); el `id` nunca cambia. */
const FRESH_KEYS = ["price", "is_taxable", "is_available"] as const

/**
 * ¿El cliente vuelve de pagar en el pago de paso (`#pago=<token>`)? Entonces el carrito
 * guardado es el de esa orden: se borra ANTES de hidratar (revisión T6, #2), aunque el
 * sondeo de la confirmación tarde o se rinda. `#pago-cancelado` no: ahí el carrito vuelve
 * del stash de sessionStorage, como hoy.
 */
export function shouldClearOnPaymentReturn(hash: string): boolean {
  return parsePaymentReturn(hash)?.kind === "paid"
}

/**
 * El controlador de la portada (puro: el hook `usePersistedGroceryCart` solo lo cablea a
 * efectos). Con `enabled: false` es inerte: ni lee ni escribe el storage.
 */
export function makeGroceryCartSync(args: { slug: string; enabled: boolean; store?: StorageLike | null }): GroceryCartSync {
  const { slug, enabled } = args
  // Sin la bandera ni siquiera se resuelve `window.localStorage`.
  const store = !enabled ? null : args.store === undefined ? defaultStore() : args.store
  let hydrated = false
  let confirmed = false
  /** La última ficha que este controlador leyó o escribió (null = nada guardado). */
  let knownRev: string | null = null
  let currentFacts: Record<string, CartLineFacts> = {}
  /** El arreglo que salió de lo guardado (hydrate/resync/stale): persistirlo no cambia nada. */
  let fromStorage: CartItem[] | null = null

  const read = () => readSnapshot(slug, store, Date.now())
  /** Adopta lo guardado: su ficha, sus datos y su carrito (que no se vuelve a escribir). */
  const adopt = (snap: Snapshot): CartItem[] => {
    knownRev = snap.rev
    currentFacts = snap.facts
    fromStorage = snap.cart
    return snap.cart
  }

  return {
    hydrate(prev) {
      if (!enabled) return prev
      hydrated = true
      // Orden pagada (en esta página o al volver del pago de paso): lo guardado ya se
      // borró; no se relee, y `prev` queda tal cual.
      if (confirmed) return prev
      const s = read()
      // La vuelta del pago cancelado (o un «Volver a pedir») ya trajo líneas: ganan. Se
      // guardan encima en el próximo persist (ya conocemos la ficha de lo guardado).
      // Nada guardado: `prev` tal cual (misma referencia, sin render de más).
      if (prev.length > 0 || s.cart.length === 0) {
        knownRev = s.rev
        currentFacts = s.facts
        return prev
      }
      return adopt(s)
    },

    resync() {
      if (!enabled || !hydrated) return null
      const s = read()
      if (s.rev === knownRev) return null
      return adopt(s)
    },

    facts() {
      return currentFacts
    },

    persist(cart, catalog) {
      // Antes de hidratar, el carrito es el `[]` del primer render: no pisa lo guardado.
      if (!enabled || !hydrated) return { kind: "skipped" }
      // Lo que acaba de salir de lo guardado no se reescribe (revisión T6, #3).
      if (cart === fromStorage) return { kind: "saved" }
      const s = read()
      if (s.rev !== knownRev) return { kind: "stale", cart: adopt(s) }
      // Los datos de cada línea: los ya guardados ganan; el catálogo de ESTA pestaña solo
      // completa lo que todavía no está. Si el catálogo pisara lo guardado, dos pestañas
      // con catálogos distintos se reescribirían sin fin vía el evento `storage` (#3).
      const byId = new Map(catalog.map((f) => [f.id, f]))
      const facts: Record<string, CartLineFacts> = {}
      for (const l of cart) {
        const f = s.facts[l.itemId] ?? byId.get(l.itemId) ?? currentFacts[l.itemId]
        if (f) facts[l.itemId] = f
      }
      // Sin cambios no se reescribe (ni ficha nueva ni evento `storage`).
      if (JSON.stringify(cart) === JSON.stringify(s.cart) && JSON.stringify(facts) === JSON.stringify(s.facts)) {
        currentFacts = facts
        return { kind: "saved" }
      }
      const rev = writeSnapshot(slug, cart, facts, store, Date.now())
      if (rev !== undefined) knownRev = rev
      currentFacts = facts
      return { kind: "saved" }
    },

    updateFacts(fresh) {
      if (!enabled) return
      const s = read()
      let changed = false
      const facts = { ...s.facts }
      for (const [id, patch] of Object.entries(fresh)) {
        const prev = facts[id]
        if (!prev) continue
        const next: CartLineFacts = { ...prev }
        for (const k of FRESH_KEYS) {
          const v = patch[k]
          if (v !== undefined && v !== prev[k]) {
            ;(next as Record<string, unknown>)[k] = v
            changed = true
          }
        }
        facts[id] = next
      }
      // Valores iguales: no se escribe (sin ficha nueva, sin evento `storage`).
      if (!changed) return
      const rev = writeSnapshot(slug, s.cart, facts, store, Date.now())
      // Si otra página guardó después de nuestra última lectura, no nos damos por
      // enterados: el próximo persist/resync tiene que ver ese cambio.
      if (s.rev === knownRev) {
        if (rev !== undefined) knownRev = rev
        currentFacts = facts
      }
    },

    confirmOrder() {
      if (!enabled) return
      clearGroceryCart(slug, store)
      confirmed = true
      knownRev = null
      currentFacts = {}
      fromStorage = null
    },
  }
}
